import React, { useState, useMemo, useEffect } from "react";
import classNames from "classnames";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Badge,
  Pagination,
  PaginationItem,
  PaginationLink,
  CardTitle,
  Col,
  Row
} from "reactstrap";
import Select from "components/Select";
import { getSelectStyles } from "../../../utils/selectStyles";
import {
  getTheme2FormFieldsCss,
  getTheme2SearchFilterSingleSelectStyles,
} from "utils/theme2MultiSelect";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";
import dayjs from "dayjs";
import { useGetNotes, useCreateNote } from "../../../api/inventory-notes/service/index.js";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import { useTranslation } from 'react-i18next';

/** 1-based page numbers; returns { type: 'page'|'ellipsis', page?, key? } */
function buildNotesPaginationItems(currentPage, pageCount) {
  const total = Math.max(1, pageCount || 1);
  const current = Math.min(Math.max(1, currentPage || 1), total);
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => ({ type: "page", page: i + 1 }));
  }
  const set = new Set([1, total]);
  for (let p = current - 1; p <= current + 1; p += 1) {
    if (p >= 1 && p <= total) set.add(p);
  }
  const sorted = [...set].sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sorted.length; i += 1) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
      out.push({ type: "ellipsis", key: `e-${sorted[i - 1]}-${sorted[i]}` });
    }
    out.push({ type: "page", page: sorted[i] });
  }
  return out;
}

const NotesTab = ({
  module,
  id,
  permission,
  activeColor = "blue",
  hideActionsSection = false,
  hidePageSizeControl = false,
  hidePagination = false,
  defaultPageSize = 10,
  isTheme2 = false,
  filterKey,
}) => {
  const { t } = useTranslation();
  const [showAddForm, setShowAddForm] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [selectedAction, setSelectedAction] = useState('');

  const activeColorStyle = getActiveColorStyle(activeColor);
  const [isLightTheme, setIsLightTheme] = useState(
    () => typeof document !== "undefined" && document.body.classList.contains("white-content")
  );

  useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.body.classList.contains('white-content'));
    };
    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [filterKey]);

  const customSelectStyles = useMemo(() => getSelectStyles(activeColorStyle, isLightTheme), [activeColorStyle, isLightTheme]);
  const selectStyles = useMemo(
    () =>
      isTheme2
        ? getTheme2SearchFilterSingleSelectStyles(activeColorStyle, true)
        : customSelectStyles,
    [isTheme2, activeColorStyle, customSelectStyles]
  );
  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? getTheme2FormFieldsCss({
            scopeSelector: ".notes-tab.notes-tab--theme2",
            fieldsSelector: ".notes-tab-fields",
          })
        : "",
    [isTheme2]
  );
  const inputStyle = isTheme2
    ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR }
    : undefined;

  const [form, setForm] = useState({
    description: '',
  });
  const [expandedMailIds, setExpandedMailIds] = useState({});

  const actionKey = selectedAction
    ? selectedAction === "Notes"
      ? "notes"
      : selectedAction === "Maintain Info"
        ? "main"
        : undefined
    : undefined;
  const key = filterKey != null && filterKey !== "" ? filterKey : actionKey;
  const { data: notes, isLoading, isError, error } = useGetNotes(page, pageSize, key, module, id);
  const { mutate: createNote } = useCreateNote();

  const handleOpenAddForm = () => {
    setForm({ description: '' });
    setShowAddForm(true);
  };

  const handleCloseAddForm = () => {
    setShowAddForm(false);
    setForm({ description: '' });
  };

  const handleAddNote = (e) => {
    e.preventDefault();
    const data = {
      description: [
        {
          type: 'paragraph',
          children: [
            { text: form.description }
          ]
        }
      ],
      [module === "deals"
        ? "deal"
        : module === "customers"
          ? "customer"
          : module === "accounts"
            ? "calculus"
            : module === "channels"
              ? "channel"
              : module === "portfolios" || module === "portfolio"
                ? "portfolio"
                : "inventory"]: id,
      type: t('notesTab.contactNote'),
      key: "notes",
      module: module,
    };

    createNote(data, {
      onSuccess: () => {
        handleCloseAddForm();
      },
      onError: (error) => {
        console.error("Error creating note:", error);
      },
    });
  };

  const renderDescription = (description) => {
    if (!Array.isArray(description)) {
      if (typeof description === "string" && description.trim()) {
        return <div style={{ marginBottom: 4, whiteSpace: "pre-wrap" }}>{description}</div>;
      }
      return null;
    }
    return description.map((block, idx) => (
      <div key={idx} style={{ marginBottom: 4 }}>
        {block.children?.map((child, childIdx) => (
          <span key={childIdx}>{child.text}</span>
        ))}
      </div>
    ));
  };

  const isMailNote = (note) => {
    const type = String(note?.note_type || note?.noteType || note?.type || "").toLowerCase();
    return type === "mail" || type === "email";
  };

  const toggleMailNote = (noteId) => {
    setExpandedMailIds((prev) => ({
      ...prev,
      [noteId]: !prev[noteId],
    }));
  };



  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  const pageCount = notes?.pagination?.pageCount || 1;
  const paginationItems = useMemo(
    () => buildNotesPaginationItems(page, pageCount),
    [page, pageCount]
  );

  if (isLoading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className='sr-only'>{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames("notes-tab", { "notes-tab--theme2": isTheme2 })}>
      {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
      <style>{`
        .notes-tab-pagination .page-item .page-link {
          border-radius: 0 !important;
        }
        .notes-tab-pagination--light .page-item:not(.active) .page-link {
          color: #1d253b !important;
          background-color: transparent !important;
        }
        .notes-tab-pagination--light .page-item:not(.active) .page-link:hover,
        .notes-tab-pagination--light .page-item:not(.active) .page-link:focus {
          color: #1d253b !important;
          background-color: rgba(154, 154, 154, 0.12) !important;
        }
        .notes-tab-pagination--dark .page-item:not(.active) .page-link {
          color: rgba(255, 255, 255, 0.88) !important;
          background-color: transparent !important;
        }
        .notes-tab-pagination--dark .page-item:not(.active) .page-link:hover,
        .notes-tab-pagination--dark .page-item:not(.active) .page-link:focus {
          color: #ffffff !important;
          background-color: rgba(255, 255, 255, 0.1) !important;
        }
      `}</style>
      <div className={classNames({ "notes-tab-fields": isTheme2 })}>
      {/* Header Section */}
      <Row style={{ marginLeft: 0, marginRight: 0 }} className="row d-flex justify-content-between align-items-center">
        {!hideActionsSection && (
          <Col md="6">
            <FormGroup>
              <Label className='form-control-label' >{t('notesTab.actions')}</Label>
              <Select
                activeColor={activeColor}
                styles={selectStyles}
                className="react-select"
                placeholder={t('notesTab.selectAction')}
                name="selectedAction"
                isSearchable={false}
                closeMenuOnSelect={true}
                value={selectedAction ? { value: selectedAction, label: selectedAction === 'Maintain Info' ? t('notesTab.maintainInfo') : t('notesTab.notes') } : null}
                onChange={(option) => setSelectedAction(option?.value || '')}
                options={[
                  { value: 'Maintain Info', label: t('notesTab.maintainInfo') },
                  { value: 'Notes', label: t('notesTab.notes') }
                ]}
                isClearable
              />
            </FormGroup>
            <div className="mt-2">
              <h6>{notes?.pagination?.total || 0} {t('notesTab.resultsFound')}</h6>
            </div>
          </Col>
        )}
        <div className={hideActionsSection ? "col-md-12 d-flex justify-content-end align-items-center" : "col-md-6 d-flex justify-content-end align-items-center"}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              {!hidePageSizeControl && (
                <Select
                  activeColor={activeColor}
                  styles={selectStyles}
                  className="react-select"
                  placeholder="Select page size"
                  name="pageSize"
                  isSearchable={false}
                  closeMenuOnSelect={true}
                  value={{ value: pageSize, label: `${pageSize} / page` }}
                  onChange={(option) => handlePageSizeChange({ target: { value: option.value } })}
                  options={[
                    { value: 10, label: '10 / page' },
                    { value: 25, label: '25 / page' },
                    { value: 50, label: '50 / page' },
                    { value: 75, label: '75 / page' },
                    { value: 100, label: '100 / page' }
                  ]}
                />
              )}
              {permission?.notes_add && (
                <Button
                  onClick={handleOpenAddForm}
                  style={{ ...activeColorStyle, color: 'white' }}
                  className="ml-3"
                >
                  <i className="tim-icons icon-simple-add mr-2" />
                  {t('notesTab.addNote')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Row>

      {/* Notes List - Only show when not adding a new note */}
      {!showAddForm && (
        <div style={{ marginLeft: 0, marginRight: 0 }} className="row">
          <div className="col-12 px-0">
            {notes?.results?.map((note) => {
              const isPromiseOpen = note.key === "promise_open";
              const isPromiseBroken = note.key === "promise_broken";
              const isPromiseKept = note.key === "promise_kept";
              const isPromise = isPromiseOpen || isPromiseBroken || isPromiseKept;
              const isMail = isMailNote(note);
              const isMailExpanded = !!expandedMailIds[note.id];

              let themeColor = "#c2dae7"; // default blueprint gray
              if (isPromiseOpen) themeColor = "#f6ad55"; // orange
              if (isPromiseBroken) themeColor = "#ff5c5c"; // red
              if (isPromiseKept) themeColor = "#16c784"; // green

              const borderColor = isPromise ? themeColor : "#c2dae7";
              const iconText = isPromise ? "PP" : (note.editor ? note.editor.substring(0, 2).toUpperCase() : "SY");
              const defaultIconColor = isLightTheme ? "#1d252b" : "#ffffff";
              const defaultIconBorder = isLightTheme ? "#1d252b" : "rgba(255, 255, 255, 0.65)";

              return (
                <Card
                  key={note.id}
                  className="mb-2"
                  style={{
                    borderLeft: `5px solid ${borderColor}`,
                    boxShadow: "none",
                  }}
                >
                  <CardBody
                    style={{
                      padding: "0.5rem"
                    }}
                  >
                    <div className="d-flex align-items-start">
                      {/* Left Circular Icon */}
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{
                          minWidth: "40px",
                          width: "40px",
                          height: "40px",
                          marginRight: "12px",
                          borderRadius: "50%",
                          border: `2px solid ${isPromise ? themeColor : defaultIconBorder}`,
                          color: isPromise ? themeColor : defaultIconColor,
                          fontWeight: "bold",
                          fontSize: "16px"
                        }}
                      >
                        {iconText}
                      </div>

                      {/* Right Content Area */}
                      <div className="flex-grow-1" style={{ fontSize: "13px", color: isLightTheme ? "#333" : "#eee" }}>
                        <div className="d-flex mb-1 mt-1">
                          <div className="font-weight-bold" style={{ width: "90px", flexShrink: 0 }}>{t('notesTab.date', { defaultValue: 'Date' })}:</div>
                          <div className="flex-grow-1">{dayjs(note.createdAt).format('MM/DD/YYYY h:mm A')}</div>
                        </div>
                        <div className="d-flex mb-1">
                          <div className="font-weight-bold" style={{ width: "90px", flexShrink: 0 }}>{t('notesTab.initials', { defaultValue: 'Initials' })}:</div>
                          <div className="flex-grow-1">{note.editor || "-"}</div>
                        </div>
                        <div className="d-flex mb-2 mt-2">
                          <div className="font-weight-bold" style={{ width: "90px", flexShrink: 0 }}>{t('notesTab.description', { defaultValue: 'Description' })}:</div>
                          <div className="flex-grow-1">
                            {isMail ? (
                              <>
                                {!isMailExpanded ? (
                                  <button
                                    type="button"
                                    onClick={() => toggleMailNote(note.id)}
                                    style={{
                                      background: "none",
                                      border: "none",
                                      padding: 0,
                                      color: activeColorStyle.borderColor || "#1d8cf8",
                                      fontWeight: 700,
                                      fontSize: "13px",
                                      cursor: "pointer",
                                      textDecoration: "underline",
                                    }}
                                  >
                                    {t("notesTab.openEmail")}
                                  </button>
                                ) : (
                                  <>
                                    {Array.isArray(note.description)
                                      ? note.description.map((block, idx) => (
                                          <div key={idx} style={{ marginBottom: 4 }}>
                                            {block.children?.map((child, childIdx) => (
                                              <span key={childIdx}>{child.text}</span>
                                            ))}
                                          </div>
                                        ))
                                      : renderDescription(note.description)}
                                    <button
                                      type="button"
                                      onClick={() => toggleMailNote(note.id)}
                                      className="mt-1"
                                      style={{
                                        background: "none",
                                        border: "none",
                                        padding: 0,
                                        color: activeColorStyle.borderColor || "#1d8cf8",
                                        fontWeight: 700,
                                        fontSize: "13px",
                                        cursor: "pointer",
                                        textDecoration: "underline",
                                      }}
                                    >
                                      {t("notesTab.closeEmail")}
                                    </button>
                                  </>
                                )}
                              </>
                            ) : (
                              Array.isArray(note.description) &&
                              note.description.map((block, idx) => {
                                // Default styling
                                let textColor = "inherit";

                                // Custom logic for promise_open, kept, broken: 3rd line onwards (idx >= 2) turn the themeColor
                                if (isPromise && idx >= 2) {
                                  textColor = themeColor;
                                }

                                return (
                                  <div key={idx} style={{ marginBottom: 4, color: textColor }}>
                                    {block.children?.map((child, childIdx) => {
                                      // Special handling if the text itself has labels like "Promise Desc:"
                                      const text = child.text || "";
                                      const splitIndex = text.indexOf(":");

                                      if (splitIndex !== -1 && isPromise && idx >= 2) {
                                        const label = text.substring(0, splitIndex + 1);
                                        const value = text.substring(splitIndex + 1);
                                        return (
                                          <span key={childIdx}>
                                            <span style={{ display: 'inline-block', width: '120px' }}>{label}</span>
                                            <span>{value}</span>
                                          </span>
                                        );
                                      }

                                      return <span key={childIdx}>{text}</span>;
                                    })}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              );
            })}

            {(!notes?.results || notes.results.length === 0) && (
              <Alert color="info">
                {t('notesTab.noNotesFound')}
              </Alert>
            )}
          </div>
        </div>
      )
      }

      {/* Pagination - Only show when not adding a new note and not hiding pagination */}
      {
        !showAddForm && !hidePagination && pageCount > 1 && (
          <div className="row mt-4">
            <div className="col-12 d-flex justify-content-center">
              <Pagination
                className={classNames("notes-tab-pagination mb-0", {
                  "notes-tab-pagination--light": isLightTheme,
                  "notes-tab-pagination--dark": !isLightTheme,
                })}
              >
                <PaginationItem disabled={page <= 1}>
                  <PaginationLink
                    previous
                    href="#prev"
                    onClick={(e) => {
                      e.preventDefault();
                      if (page > 1) handlePageChange(page - 1);
                    }}
                  />
                </PaginationItem>
                {paginationItems.map((item) =>
                  item.type === "ellipsis" ? (
                    <PaginationItem key={item.key} disabled>
                      <PaginationLink href="#ellipsis" onClick={(e) => e.preventDefault()}>
                        …
                      </PaginationLink>
                    </PaginationItem>
                  ) : (
                    <PaginationItem key={item.page} active={item.page === page}>
                      <PaginationLink
                        href={`#page-${item.page}`}
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(item.page);
                        }}
                        style={
                          item.page === page
                            ? { ...activeColorStyle, color: "#fff", border: 0 }
                            : undefined
                        }
                      >
                        {item.page}
                      </PaginationLink>
                    </PaginationItem>
                  )
                )}
                <PaginationItem disabled={page >= pageCount}>
                  <PaginationLink
                    next
                    href="#next"
                    onClick={(e) => {
                      e.preventDefault();
                      if (page < pageCount) handlePageChange(page + 1);
                    }}
                  />
                </PaginationItem>
              </Pagination>
            </div>
          </div>
        )
      }

      {/* Add Note Form */}
      {
        showAddForm && (
          <Card >
            <CardHeader>
              <div className="d-flex justify-content-between align-items-center">
                <CardTitle>
                  <i className="tim-icons icon-simple-add mr-2" />
                  {t('notesTab.addNewNote')}
                </CardTitle>
                <Button
                  color="secondary"
                  size="sm"
                  onClick={handleCloseAddForm}
                >
                  <i className="tim-icons icon-simple-remove mr-1" />
                  {t('notesTab.cancel')}
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              <Form onSubmit={handleAddNote}>
                <FormGroup>
                  <Label className="form-control-label">{t('notesTab.description')}</Label>
                  <Input
                    className={isTheme2 ? "form-control" : undefined}
                    style={inputStyle}
                    type="textarea"
                    rows={6}
                    value={form.description}
                    onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder={t('notesTab.enterNoteDescription')}
                  />
                </FormGroup>
                <div className="d-flex justify-content-end mt-4">
                  <Button
                    type="submit"
                    style={{ ...activeColorStyle, color: 'white' }}
                  >
                    <i className="tim-icons icon-check-2 mr-2" />
                    {t('notesTab.saveNote')}
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        )
      }
      </div>
    </div>
  );
};

export default NotesTab; 